import { isServer } from '@unifyapps/network/utils/fetch/utils';
import isNative from '@unifyapps/network/utils/native/isNative';
import { isJavaScriptReservedWord } from './entityValidation/reservedWords';
import { getReadOnlySafeGlobalContext } from './readOnlyContext/readOnlySafeGlobalContext';
import { safeGlobalContext } from './safeContext';

export type EvalMode = 'readOnly' | 'readWrite';

export interface EvalOptions {
  evalMode?: EvalMode;
}

// readOnly is deliberately the default: an eval call site that forgets to declare its
// mode fails CLOSED (formula semantics), never open. read-write is reachable only by
// explicitly passing evalMode — the deliberate script surfaces (evalJS / executeScript
// / page functions) opt in that way. There is no global runtime override.
const resolveBaseContext = (evalMode?: EvalMode): Record<string, unknown> => {
  if (evalMode === 'readWrite') {
    return safeGlobalContext as unknown as Record<string, unknown>;
  }
  return getReadOnlySafeGlobalContext();
};

const VALID_JS_IDENTIFIER_REGEX = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;
// Reserved as binding targets under "use strict" but absent from the ES5 set:
// strict-mode FutureReservedWords plus `arguments`/`eval`.
const STRICT_MODE_RESERVED_BINDINGS = new Set([
  'implements',
  'interface',
  'let',
  'package',
  'private',
  'protected',
  'public',
  'static',
  'yield',
  'arguments',
  'eval',
]);

// Memo for isSafeBindingIdentifier — the result is a pure function of the key
// string, and the same context keys (block ids, the fixed frame) recur on every
// single evaluate call across the page. Without this, each evaluator build
// re-runs a regex + two Set lookups over ~300 keys; caching turns that into one
// Map hit per key. Bounded by the count of distinct context keys seen (page
// blocks + materialized runtime ids), which is small and stable.
const bindingIdentifierCache = new Map<string, boolean>();

// True when `key` can be a bare binding/param name under "use strict". Keys that
// fail this (reserved words, invalid identifiers like `content-type`) would throw
// when spliced into the generated Function, collapsing every binding to undefined.
function isSafeBindingIdentifier(key: string): boolean {
  const cached = bindingIdentifierCache.get(key);
  if (cached !== undefined) return cached;
  const result =
    VALID_JS_IDENTIFIER_REGEX.test(key) &&
    !isJavaScriptReservedWord(key) &&
    !STRICT_MODE_RESERVED_BINDINGS.has(key);
  bindingIdentifierCache.set(key, result);
  return result;
}

// Filtered (key, value) pairs for `new Function(...keys, body)` callers — kept
// positionally aligned. A dropped key can never be a bare identifier anyway.
function getSafeBindings(context: Record<string, unknown>): {
  keys: string[];
  values: unknown[];
} {
  const keys: string[] = [];
  const values: unknown[] = [];
  for (const key of Object.keys(context)) {
    if (!isSafeBindingIdentifier(key)) continue;
    keys.push(key);
    values.push(context[key]);
  }
  return { keys, values };
}

// To eval a single JavaScript expression or statement and get its resolved value use indirectEvalExpression
export const indirectEvalExpression = (
  script: string,
  globalContext: object,
  options?: EvalOptions,
): unknown => {
  const context = { ...resolveBaseContext(options?.evalMode), ...globalContext };
  const { keys, values } = getSafeBindings(context);
  // escape newline characters to avoid "Invalid or unexpected token" error
  // we need to preserve the newline characters in the script to maintain formatting, eg. markdown default value
  // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func -- needed
  const evalFunction = new Function(
    ...keys,
    `"use strict"; return ${script.replace(/\n/g, '\\n')}`,
  );
  return evalFunction(...values);
};

export const evaluatorCache = new Map<string, (...args: unknown[]) => unknown>();

function setEvaluatorCache(cacheKey: string, evalFunction: (...args: unknown[]) => unknown) {
  if (isServer() && !isNative()) {
    return;
  }
  evaluatorCache.set(cacheKey, evalFunction);
}

export const createExpressionEvaluator = (
  globalContext: Record<string, unknown>,
  options?: EvalOptions,
): ((script: string) => unknown) => {
  // Keys and values MUST come from this one merged object: deriving the destructure
  // list on a separate line from the value merge is how a shadow silently stops
  // binding while every test on the other entry point stays green. Deduping through
  // Object.keys also prevents a duplicate-binding SyntaxError when a caller context
  // overrides a base key.
  const mergedContext = { ...resolveBaseContext(options?.evalMode), ...globalContext };
  // Drop keys that aren't legal strict-mode binding names — they'd throw in the
  // destructure below, collapsing every binding in the context to undefined.
  const mergedKeys = Object.keys(mergedContext).filter(isSafeBindingIdentifier);

  return (script: string): unknown => {
    if (typeof script !== 'string') {
      throw new TypeError('Script must be a string');
    }

    const destructureStmt = `const { ${mergedKeys.join(', ')} } = context;`;
    const finalScript = `\nreturn (${script.replace(/\n/g, '\\n')});`;
    const completeScript = `"use strict";\n${destructureStmt}\n${finalScript}`;

    let evalFunction = evaluatorCache.get(completeScript);
    if (!evalFunction) {
      // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
      evalFunction = new Function('context', completeScript) as (
        context: Record<string, unknown>,
      ) => unknown;

      setEvaluatorCache(completeScript, evalFunction);
    }

    return evalFunction(mergedContext);
  };
};

export type ExpressionMap = Record<string, string>;
export type EvaluatedMap<T extends ExpressionMap> = {
  [K in keyof T]: unknown;
};

const validateExpressions = (expressions: Record<string, unknown>): void => {
  const scripts = Object.values(expressions);
  for (let i = 0; i < scripts.length; i++) {
    if (typeof scripts[i] !== 'string') {
      // console.debug('scripts', scripts);
      throw new TypeError('Expression must be a string');
    }
  }
};

interface ComplexEvaluatorPrep {
  destructureLine: string;
  mergedContext: Record<string, unknown>;
}

// The per-context prep (the safe-key filter, the destructure line, and the merged
// context) is a pure function of `globalContext` AND the eval mode. During a render
// pass the same context object is handed to this evaluator many times (measured ~60%
// of calls reuse a ref), so caching the prep by context identity skips it on every
// repeat. Split by mode: the prep bakes in that mode's base context, so a ref reused
// across readOnly and readWrite must not share prep, or one mode silently runs with
// the other's base bindings. WeakMap so entries vanish with their context.
const complexEvaluatorPrepCache = {
  readOnly: new WeakMap<object, ComplexEvaluatorPrep>(),
  readWrite: new WeakMap<object, ComplexEvaluatorPrep>(),
};

export const createComplexExpressionsEvaluator = <T extends ExpressionMap>(
  globalContext: Record<string, unknown>,
  options?: EvalOptions,
): ((expressions: T) => EvaluatedMap<T>) => {
  // Pick the mode's prep cache (see the cache declaration): a globalContext ref reused
  // across readOnly and readWrite must not share prep, or one mode silently runs with
  // the other's base bindings.
  const prepCache =
    options?.evalMode === 'readWrite'
      ? complexEvaluatorPrepCache.readWrite
      : complexEvaluatorPrepCache.readOnly;
  let prep = prepCache.get(globalContext);
  if (!prep) {
    // Single-source rule (see createExpressionEvaluator): destructure keys and merged
    // values both come from this one object, so a shadow can never silently unbind and
    // a caller key overriding a base key can't produce a duplicate binding. Also drops
    // keys that aren't legal strict-mode bindings so one bad key can't collapse the
    // whole batch to {}.
    const mergedBaseContext = { ...resolveBaseContext(options?.evalMode), ...globalContext };
    const safeBaseKeys = Object.keys(mergedBaseContext).filter(isSafeBindingIdentifier);
    prep = {
      destructureLine: `const { ${safeBaseKeys.join(', ')} } = context;`,
      mergedContext: mergedBaseContext,
    };
    prepCache.set(globalContext, prep);
  }
  const { destructureLine, mergedContext } = prep;

  const createOptimizedEvaluator = (expressions: Record<string, string>) => {
    const uniqueExpressions = new Map<string, Set<string>>();
    const exprKeys = Object.keys(expressions);
    const exprKeysLength = exprKeys.length;
    for (let i = 0; i < exprKeysLength; i++) {
      const key = exprKeys[i];
      const expr = expressions[key];
      let existingKeys = uniqueExpressions.get(expr);
      if (!existingKeys) {
        existingKeys = new Set();
        uniqueExpressions.set(expr, existingKeys);
      }
      existingKeys.add(key);
    }

    let finalScript: string;

    if (uniqueExpressions.size === 1) {
      let expr = '';
      let keySet = new Set<string>();
      for (const [e, k] of uniqueExpressions.entries()) {
        expr = e;
        keySet = k;
        break;
      }

      const assignmentParts: string[] = new Array(keySet.size);
      let assignmentIdx = 0;
      for (const k of keySet) {
        assignmentParts[assignmentIdx++] = `__result["${k}"] = __value;`;
      }

      finalScript = `"use strict";
        const __result = {};
        const __value = ${expr.replace(/\n/g, '\\n')};
        ${assignmentParts.join('\n')}
        return __result;`;
    } else {
      let totalKeys = 0;
      for (const keySet of uniqueExpressions.values()) {
        totalKeys += keySet.size;
      }
      const estimatedSize = 2 + uniqueExpressions.size * 4 + totalKeys + 1;
      const parts: string[] = new Array(estimatedSize);
      let idx = 0;
      parts[idx++] = '"use strict";';
      parts[idx++] = 'const __result = {};';

      for (const [expr, keySet] of uniqueExpressions.entries()) {
        parts[idx++] = 'try {';
        parts[idx++] = `  const __value = ${expr.replace(/\n/g, '\\n')};`;
        for (const k of keySet) {
          parts[idx++] = `  __result["${k}"] = __value;`;
        }
        parts[idx++] = '} catch (e) {}';
      }
      parts[idx++] = 'return __result;';

      finalScript = parts.slice(0, idx).join('\n');
    }

    const completeScript = `"use strict";\n${destructureLine}\n${finalScript}`;
    const cacheKey = completeScript;

    const cached = evaluatorCache.get(cacheKey);
    if (cached) return cached as (context: Record<string, unknown>) => EvaluatedMap<T>;

    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func -- needed
    const evaluator = new Function('context', completeScript) as (
      context: Record<string, unknown>,
    ) => EvaluatedMap<T>;

    setEvaluatorCache(cacheKey, evaluator);
    return evaluator;
  };

  return (expressions: T): EvaluatedMap<T> => {
    validateExpressions(expressions);
    const evaluator = createOptimizedEvaluator(expressions);
    return evaluator(mergedContext) as EvaluatedMap<T>;
  };
};

// To execute any JavaScript code, which doesn't return anything with global context use indirectExecuteScript
export const indirectExecuteScript = (
  script: string,
  globalContext: object,
  exposedWindowVariables?: string[],
) => {
  const context = { ...safeGlobalContext, ...globalContext };

  if (exposedWindowVariables) {
    for (let i = 0; i < exposedWindowVariables.length; i++) {
      const key = exposedWindowVariables[i];
      // omitting keys that are present in safeglobalcontext because
      // we want our implementation
      if (typeof window[key] !== 'undefined' && !safeGlobalContext.window[key]) {
        context.window[key] = window[key] as unknown;
      }
    }
  }

  const { keys, values } = getSafeBindings(context);
  // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func -- needed
  const evalFunction = new Function(...keys, `"use strict"; ${script}`);
  evalFunction(...values);
};
